// @file Slideshow realtime store
// - Owns the reactive state (currentPresenter, slideshowSessionSlideId, isFollowingPresenter, etc.)
// - Manages the channel lifecycle and message handling
// - Has no Vue component coupling — no onMounted, no watchers
import { getVuexStore } from '@@/bits/pinia'
import { RootState } from '@@/vuexstore/surface/types'
import type * as Phoenix from 'phoenix'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useSurfaceStore } from './surface'
import { useSurfaceCurrentUserStore } from './surface_current_user'

type BroadcastCurrentSlideMessage = Record<string, number> // user_id => slide_id

type SpotlightTransferRequestPayload = { requester_id: string; requester_name: string }

type CurrentPresenter = { id: number; name: string }

type SlideshowChannelMessage =
  | { event: 'broadcast_current_slide'; uid?: string; message: BroadcastCurrentSlideMessage }
  | { event: 'latest_slide_broadcasted'; uid?: string; message: BroadcastCurrentSlideMessage }
  | { event: 'current_presenter'; uid?: string; message: { id: string; name: string } | null }
  | { event: 'presenter_stopped'; uid?: string; message: Record<string, never> }
  | { event: 'spotlight:transfer_requested'; uid?: string; message: SpotlightTransferRequestPayload }
  | { event: 'spotlight:transferred'; uid?: string; message: { new_presenter_id: string; new_presenter_name: string } }
  | { event: 'spotlight:transfer_denied'; uid?: string; message: Record<string, never> }
  | { event: 'spotlight:transfer_cancelled'; uid?: string; message: Record<string, never> }
  | { event: 'current_transfer_request'; uid?: string; message: SpotlightTransferRequestPayload | null }
  | { event: 'viewer:slide_changed'; uid?: string; message: { user_id: string; slide_index: number | null } }
  | { event: 'current_viewer_positions'; uid?: string; message: Record<string, number> }

const useSlideshowRealtimeStore = defineStore('slideshowRealtime', () => {
  const surfaceStore = useSurfaceStore()
  const currentDeviceUid = computed(() => surfaceStore.uid)

  const currentUserStore = useSurfaceCurrentUserStore()
  const currentUserId = computed(() => currentUserStore.currentUser.id)

  // ── Channel connection ───────────────────────────────────────────────────────
  const surfaceVuexStore = getVuexStore<RootState>()
  const brahmsSocket = computed(
    () => surfaceVuexStore?.state.realtime?.brahmsSocket as Phoenix.Socket | null | undefined,
  )
  const slideshowChannel = ref<Phoenix.Channel | null>(null)

  async function connectToSlideshowSession(wallId: number): Promise<void> {
    if (slideshowChannel.value != null) {
      await disconnectFromSlideshowSession()
    }
    return await new Promise((resolve, _reject) => {
      slideshowChannel.value = brahmsSocket.value?.channel(`slideshow:${wallId}`) ?? null
      if (slideshowChannel.value == null) {
        resolve()
        return
      }
      slideshowChannel.value.on('new_msg', (data) => {
        handleSlideshowChannelMessage(data)
      })
      slideshowChannel.value.join().receive('ok', () => {
        resolve()
      })
    })
  }

  async function disconnectFromSlideshowSession(): Promise<void> {
    return await new Promise((resolve, _reject) => {
      slideshowSessionSlideId.value = null
      currentPresenter.value = null
      broadcastedSlidesMap.value = {}
      isFollowingPresenter.value = true
      spotlightRequester.value = null
      viewerSlides.value = {}
      if (slideshowChannel.value == null) {
        resolve()
        return
      }
      slideshowChannel.value.leave().receive('ok', () => {
        slideshowChannel.value = null
        resolve()
      })
    })
  }

  // ── Presenter & slide tracking ─────────────────────────────────────────────
  const currentPresenter = ref<CurrentPresenter | null>(null)
  const slideshowSessionSlideId = ref<number | null>(null)
  const broadcastedSlidesMap = ref<BroadcastCurrentSlideMessage>({})

  const isPresenter = computed(() => currentPresenter.value?.id === currentUserId.value)

  function setPresenterCurrentSlide(payload: BroadcastCurrentSlideMessage): void {
    if (currentPresenter.value == null) return
    slideshowSessionSlideId.value = payload[currentPresenter.value.id] ?? null
  }

  function setBroadcastedSlide(payload: BroadcastCurrentSlideMessage): void {
    broadcastedSlidesMap.value = { ...broadcastedSlidesMap.value, ...payload }
    setPresenterCurrentSlide(payload)
  }

  function pushBroadcastCurrentSlide(slideId: number): void {
    if (slideId == null) return
    if (currentPresenter.value == null) return
    if (currentPresenter.value.id !== currentUserId.value) return
    if (slideshowChannel.value == null) return
    slideshowChannel.value.push('broadcast_current_slide', { user_id: currentUserId.value, slide_id: slideId })
  }

  // ── Spotlight ──────────────────────────────────────────────────────────────
  const isSpotlightActive = computed(() => currentPresenter.value != null)
  const isCurrentUserLockedToPresenter = computed(
    () => isSpotlightActive.value && isFollowingPresenter.value && !(surfaceVuexStore?.getters.canIEdit as boolean),
  )

  function startSpotlight(presenterName: string): void {
    slideshowChannel.value?.push('start_spotlight', { presenter_name: presenterName })
  }

  function stopSpotlight(): void {
    slideshowChannel.value?.push('stop_spotlight', {})
  }

  // ── Spotlight transfer ─────────────────────────────────────────────────────
  const spotlightRequester = ref<{ id: string; name: string } | null>(null)
  const wasRequestDenied = ref(false)

  const isCurrentUserRequester = computed(() => spotlightRequester.value?.id === String(currentUserId.value))

  function requestTransfer(requesterName: string): void {
    slideshowChannel.value?.push('spotlight_request_transfer', { requester_name: requesterName })
  }

  function acceptTransfer(): void {
    slideshowChannel.value?.push('spotlight_accept_transfer', {})
  }

  function denyTransfer(): void {
    slideshowChannel.value?.push('spotlight_deny_transfer', {})
  }

  function cancelRequest(): void {
    slideshowChannel.value?.push('spotlight_cancel_request', {})
  }

  // ── Viewer position tracking ───────────────────────────────────────────────
  const viewerSlides = ref<Record<string, number>>({})
  const isFollowingPresenter = ref<boolean>(true)

  function pushViewerSlideUpdate(slideIndex: number): void {
    slideshowChannel.value?.push('viewer_slide_update', { slide_index: slideIndex })
  }

  // ── Message handler ────────────────────────────────────────────────────────
  function handleSlideshowChannelMessage(messageObj?: SlideshowChannelMessage): void {
    if (messageObj == null) return
    if (messageObj.uid != null && messageObj.uid === currentDeviceUid.value) return

    switch (messageObj.event) {
      // broadcast_current_slide: real-time fan-out to all clients when the presenter navigates
      // latest_slide_broadcasted: point-to-point catch-up push to a newly joined client
      case 'broadcast_current_slide':
      case 'latest_slide_broadcasted':
        setBroadcastedSlide(messageObj.message)
        break
      case 'current_presenter':
        currentPresenter.value =
          messageObj.message != null ? { id: Number(messageObj.message.id), name: messageObj.message.name } : null
        setPresenterCurrentSlide(broadcastedSlidesMap.value)
        break
      case 'presenter_stopped':
        currentPresenter.value = null
        slideshowSessionSlideId.value = null
        isFollowingPresenter.value = true
        spotlightRequester.value = null
        break
      case 'spotlight:transfer_requested':
        spotlightRequester.value = { id: messageObj.message.requester_id, name: messageObj.message.requester_name }
        break
      case 'spotlight:transferred':
        currentPresenter.value = {
          id: Number(messageObj.message.new_presenter_id),
          name: messageObj.message.new_presenter_name,
        }
        spotlightRequester.value = null
        setPresenterCurrentSlide(broadcastedSlidesMap.value)
        break
      case 'spotlight:transfer_denied':
        wasRequestDenied.value = true
        spotlightRequester.value = null
        break
      case 'spotlight:transfer_cancelled':
        spotlightRequester.value = null
        break
      case 'current_transfer_request':
        if (messageObj.message != null) {
          spotlightRequester.value = { id: messageObj.message.requester_id, name: messageObj.message.requester_name }
        } else {
          spotlightRequester.value = null
        }
        break
      case 'viewer:slide_changed':
        if (messageObj.message.slide_index == null) {
          const { [messageObj.message.user_id]: _, ...rest } = viewerSlides.value
          viewerSlides.value = rest
        } else {
          viewerSlides.value = { ...viewerSlides.value, [messageObj.message.user_id]: messageObj.message.slide_index }
        }
        break
      case 'current_viewer_positions':
        viewerSlides.value = { ...messageObj.message }
        break
    }
  }

  return {
    currentUserId,
    currentPresenter,
    slideshowSessionSlideId,
    isFollowingPresenter,
    broadcastedSlidesMap,
    spotlightRequester,
    viewerSlides,
    isPresenter,
    isSpotlightActive,
    isCurrentUserRequester,
    wasRequestDenied,
    isCurrentUserLockedToPresenter,
    connectToSlideshowSession,
    disconnectFromSlideshowSession,
    startSpotlight,
    stopSpotlight,
    requestTransfer,
    acceptTransfer,
    denyTransfer,
    cancelRequest,
    pushBroadcastCurrentSlide,
    pushViewerSlideUpdate,
  }
})
export { useSlideshowRealtimeStore }
